import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import '../../../../../assets/scss/image-gallery-custom.scss';
import { getExtension } from '../../../../../Components/WebChat/Common/FileUploadValidation';
import getFileFromType from '../../../../../Components/WebChat/Conversation/Templates/Common/getFileFromType';
import { millisToMinutesAndSeconds } from '../../../../../Helpers/Utility';
import { closeMediaPreview, setSelectedIndex } from '../../../../../Slice';
import { AudioFile, AudioRecord, PreviewClose, PreviewDownload, PreviewStar } from '../../../../rootAccess';
import { downloadMediaAndTriggerSave } from '../../../../utils';
import { useMergedMediaMessages } from '../../../../utils/customHooks/useMediaMessages';
import AudioPreview from './AudioPreview';
import ImagePreview from './ImagePreview';
import VideoPreview from './VideoPreview';
import './WebChannelMediaPreview.scss';

const WebChannelMediaPreview = () => {
   const dispatch = useDispatch();
   const { channelId, selectedMessage } = useSelector(state => state.channelMediaPreviewData);
   const { mergedMediaMessages: displayMediaList } = useMergedMediaMessages(channelId, [
      'image',
      'video',
      'audio',
      'file',
   ]);

   const mappedSelectedIndex = useMemo(() => {
      if (!selectedMessage || !displayMediaList.length) {
         return 0;
      }

      const selectedMsgId = selectedMessage.messageId || selectedMessage.msgId;

      if (!selectedMsgId) {
         return 0;
      }

      const index = displayMediaList.findIndex(
         media => media.messageId === selectedMsgId || media.msgId === selectedMsgId
      );

      return index >= 0 ? index : 0;
   }, [selectedMessage, displayMediaList]);

   const [selectedItem, setSelectedItem] = useState(mappedSelectedIndex);
   const [lastTranslateX, setLastTranslateX] = React.useState(false);
   const [viewAllMedias] = useState(true);
   const handlePrevious = useRef();
   const handleNextMedia = useRef();

   const handleClose = useCallback(() => {
      dispatch(closeMediaPreview());
   }, [dispatch]);

   const getTransformMatrix = targetThumb => {
      const style = targetThumb.style.getPropertyValue('transform');
      return new DOMMatrixReadOnly(style);
   };

   const setTransformMatrix = (targetThumb, matrixM41) => {
      targetThumb.style = `transform: translate3d(${Math.abs(matrixM41)}px, 0, 0)`;
      setLastTranslateX(matrixM41);
   };

   const handleStyleChange = targetThumb => {
      const { m41: initialM41 } = getTransformMatrix(targetThumb);
      setTransformMatrix(targetThumb, initialM41);
      const mutationObserver = new MutationObserver(mutations => {
         mutations.forEach(mutationRecord => {
            const { m41: newM41 } = getTransformMatrix(mutationRecord.target);
            if (lastTranslateX !== newM41 && newM41) {
               setTransformMatrix(targetThumb, newM41);
               mutationObserver.disconnect();
            }
            mutationObserver.observe(targetThumb, { attributes: true, attributeFilter: ['style'] });
         });
      });
      mutationObserver.observe(targetThumb, { attributes: true, attributeFilter: ['style'] });
   };

   const initializeMutation = () => {
      let thumbInterval = setInterval(() => {
         const targetThumb = document.querySelector('.thumbs.animated');
         if (targetThumb) {
            handleStyleChange(targetThumb);
            clearInterval(thumbInterval);
            thumbInterval = 0;
         }
      }, 100);
   };

   React.useEffect(() => {
      initializeMutation();
   }, []);

   const handleOnKeyPress = useCallback((event = {}) => {
      switch (event.keyCode) {
         case 27: // ESC
            handleClose();
            break;
         case 37: // Left arrow
            handlePrevious?.current?.click();
            break;
         case 39: // Right arrow
            handleNextMedia?.current?.click();
            break;
         default:
            break;
      }
      return true;
   }, []);

   useEffect(() => {
      document.addEventListener('keydown', handleOnKeyPress, false);
      return () => {
         document.removeEventListener('keydown', handleOnKeyPress, false);
      };
   }, [handleOnKeyPress]);

   useEffect(() => {
      setSelectedItem(mappedSelectedIndex);
   }, [mappedSelectedIndex]);

   const videoControls = (fileUrl, msgId, thumbImage, autoplay) => {
      return {
         autoPlay: false,
         msgId: msgId,
         controls: true,
         sources: [
            {
               src: fileUrl,
               type: 'video/mp4',
            },
         ],
         poster: thumbImage,
      };
   };

   const handleMediaOnChange = (index, item) => {
      setSelectedItem(index);
      dispatch(setSelectedIndex(index));
      const elems = document?.querySelectorAll?.('.AudioDetails');
      [].forEach.call(elems, function (el) {
         el.classList.add('default');
         el.classList.remove('playAudio');
      });
      document
         .getElementById('imagePreviewContainer')
         .querySelectorAll('video, audio')
         .forEach(element => {
            if (element) {
               let dataMsgId = element.getAttribute('data-msgid');
               if (dataMsgId && dataMsgId + index === item.props.id) {
                  element.play();
               } else {
                  element.currentTime = 0;
                  element.pause();
               }
            }
         });
   };

   const handlePrev = (onClickHandler, hasPrev) => {
      return (
         viewAllMedias &&
         hasPrev && (
            <button
               type="button"
               onClick={onClickHandler}
               title="Previous"
               ref={input => (handlePrevious.current = input)}
               aria-label="previous slide / item"
               className="control-arrow control-prev"></button>
         )
      );
   };

   const handleNext = (onClickHandler, hasNext) => {
      return (
         viewAllMedias &&
         hasNext && (
            <button
               type="button"
               onClick={onClickHandler}
               title="Next"
               ref={input => (handleNextMedia.current = input)}
               aria-label="next slide / item"
               className="control-arrow control-next"></button>
         )
      );
   };

   const formatFileSize = useCallback(bytes => {
      if (!bytes) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
   }, []);

   const renderMediaItem = (media, index) => {
      const { messageType, content, messageId } = media;
      const { media: mediaData = {} } = content || {};
      const { fileName, file_url, file_size, thumb_image, audioType } = mediaData;
      let thumbImage = getThumbnailUrl(thumb_image);
      let mediaContent = null;
      let mediaClass = '';
      const fileExtension = getExtension(fileName);
      const placeholder = getFileFromType(null, fileExtension);

      switch (messageType) {
         case 'image':
            mediaClass = 'type-image';
            mediaContent = (
               <ImagePreview
                  media={media}
                  channelId={channelId}
                  index={index}
                  isActive={selectedItem === index}
                  thumbImage={thumbImage}
                  selectedIndex={selectedItem}
               />
            );
            break;

         case 'video':
            mediaClass = 'type-media videos';
            mediaContent = (
               <VideoPreview
                  media={media}
                  channelId={channelId}
                  index={index}
                  isActive={selectedItem === index}
                  thumbImage={thumbImage}
                  videoControls={videoControls}
               />
            );
            break;

         case 'audio':
            thumbImage = audioType !== 'recording' ? AudioFile : AudioRecord;
            mediaClass = 'type-media audio';
            mediaContent = (
               <AudioPreview
                  media={media}
                  channelId={channelId}
                  isActive={selectedItem === index}
                  videoControls={videoControls}
               />
            );
            break;

         case 'file':
            thumbImage = placeholder;
            mediaClass = 'type-image file';
            mediaContent = (
               <div className="type-thumb-image">
                  <div className="file-preview">
                     <div className="file-icon">📄</div>
                     <div className="file-info">
                        <p className="document-name">{`${fileName}, ${formatFileSize(file_size)}`}</p>
                     </div>
                  </div>
               </div>
            );
            break;

         default:
            return null;
      }

      return (
         <div key={messageId} className={mediaClass} id={messageId}>
            <img style={{ display: 'none' }} src={thumbImage || file_url} className="data" alt={fileName} />
            {mediaContent}
         </div>
      );
   };

   const getThumbnailUrl = thumb => {
      if (!thumb) return null;
      if (thumb.startsWith('data:') || thumb.startsWith('http')) return thumb;
      return `data:image/jpeg;base64,${thumb}`;
   };

   const customRenderThumb = React.useCallback(
      children => {
         if (!viewAllMedias) return null;
         return children.map((item, index) => {
            const url = item.props?.children[0]?.props?.src;
            const media = displayMediaList[index] || {};            
            const { messageType, content, messageId } = media;
            const { media: mediaData = {} } = content || {};
            const { duration, fileName } = mediaData;
            const iconClassMap = {
               video: 'video-icon',
               file: 'File',
            };
            return (
               <div
                  key={`${channelId}-${messageId}`}
                  className={`thumb-img ${iconClassMap[messageType] || ''} img-load`}>
                  <img
                     src={url}
                     alt={fileName || 'Thumbnail'}
                     key={`${messageId}-Img`}
                     style={{ display: 'block' }}
                     onError={e => {
                        console.warn('Thumbnail failed to load:', e.target.src);
                        e.target.src = 'https://placehold.co/120x90/cccccc/666666?text=Mirrofly';
                     }}
                  />
                  {duration && <p className="audio-duration">{millisToMinutesAndSeconds(duration)}</p>}
               </div>
            );
         });
      },
      [displayMediaList, viewAllMedias],
   );

   const downloadFile = () => {
      if (selectedItem > -1 && displayMediaList.length > 0) {
         const { messageId = '' } = displayMediaList[selectedItem];
         downloadMediaAndTriggerSave(channelId, messageId);
      }
   };

   const previewData = displayMediaList.map((media, index) => renderMediaItem(media, index));

   return (
      <div className="webChannelMediaPreview">
         <div className="imagePreviewContainer imagePreviewWrapper" id="imagePreviewContainer">
            <div className="preview-options">
               <ul>
                  <li>
                     <button
                        className="preview-btn"
                        title="Download"
                        onClick={downloadFile}
                        onKeyDown={e => e.key === 'Enter' && downloadFile}>
                        <PreviewDownload />
                     </button>
                  </li>

                  {/* <li>
                     <i className="preview-btn icon-star">
                        <PreviewStar fill="#fff" />
                     </i>
                  </li> */}

                  <li>
                     <button
                        className="preview-btn preview-close"
                        title="Close"
                        onClick={handleClose}
                        onKeyDown={e => e.key === 'Enter' && handleClose()}>
                        <PreviewClose />
                     </button>
                  </li>
               </ul>
            </div>

            {previewData.length > 0 && (
               <Carousel
                  key={displayMediaList[0]?.messageId}
                  renderArrowPrev={handleNext}
                  renderArrowNext={handlePrev}
                  selectedItem={selectedItem}
                  onChange={handleMediaOnChange}
                  renderThumbs={customRenderThumb}
                  transitionTime={10}
                  thumbWidth={120}
                  swipeable={false}>
                  {previewData}
               </Carousel>
            )}
         </div>
      </div>
   );
};

export default WebChannelMediaPreview;
